import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file.');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Database helper functions
export const db = {
  // Customer operations
  customers: {
    async create(customerData) {
      const { data, error } = await supabase
        .from('customers')
        .insert([customerData])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },

    async getAll() {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },

    async getById(id) {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data;
    },

    async update(id, updates) {
      const { data, error } = await supabase
        .from('customers')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    }
  },

  // Clothing items operations
  clothingItems: {
    async getAll() {
      const { data, error } = await supabase
        .from('clothing_items')
        .select('*')
        .order('category', { ascending: true });
      
      if (error) throw error;
      return data;
    },

    async create(itemData) {
      const { data, error } = await supabase
        .from('clothing_items')
        .insert([itemData])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },

    async update(id, updates) {
      const { data, error } = await supabase
        .from('clothing_items')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    }
  },

  // Orders operations
  orders: {
    async create(orderData) {
      const { data, error } = await supabase
        .from('orders')
        .insert([orderData])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },

    async getAll() {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          customers (
            full_name,
            email,
            phone,
            address_line1,
            city,
            service_area
          ),
          order_items (
            *,
            clothing_items (
              name,
              category
            )
          )
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },

    async getById(id) {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          customers (*),
          order_items (
            *,
            clothing_items (*)
          )
        `)
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data;
    },

    async updateStatus(id, status) {
      const { data, error } = await supabase
        .from('orders')
        .update({ status })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },

    async updatePaymentStatus(id, paymentStatus, paymentIntentId = null) {
      const updates = { payment_status: paymentStatus };
      if (paymentIntentId) {
        updates.payment_intent_id = paymentIntentId;
      }

      const { data, error } = await supabase
        .from('orders')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    }
  },

  // Order items operations
  orderItems: {
    async create(orderItemData) {
      const { data, error } = await supabase
        .from('order_items')
        .insert([orderItemData])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },

    async getByOrderId(orderId) {
      const { data, error } = await supabase
        .from('order_items')
        .select(`
          *,
          clothing_items (*)
        `)
        .eq('order_id', orderId);
      
      if (error) throw error;
      return data;
    }
  },

  // Communication templates operations
  communicationTemplates: {
    async getAll() {
      const { data, error } = await supabase
        .from('communication_templates')
        .select('*')
        .order('type', { ascending: true })
        .order('status_trigger', { ascending: true });
      
      if (error) throw error;
      return data;
    },

    async create(templateData) {
      const { data, error } = await supabase
        .from('communication_templates')
        .insert([templateData])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },

    async update(id, updates) {
      const { data, error } = await supabase
        .from('communication_templates')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },

    async delete(id) {
      const { error } = await supabase
        .from('communication_templates')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return true;
    }
  },

  // Communication history operations
  communicationHistory: {
    async getByCustomerId(customerId) {
      const { data, error } = await supabase
        .from('communication_history')
        .select(`
          *,
          orders (
            order_number
          )
        `)
        .eq('customer_id', customerId)
        .order('sent_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },

    async getByOrderId(orderId) {
      const { data, error } = await supabase
        .from('communication_history')
        .select('*')
        .eq('order_id', orderId)
        .order('sent_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },

    async create(historyData) {
      const { data, error } = await supabase
        .from('communication_history')
        .insert([historyData])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    }
  },

  // Blog articles operations
  blogArticles: {
    // Helper function to compute additional fields
    computeArticleFields(article) {
      if (!article) return article;
      
      return {
        ...article,
        // Generate excerpt from content (first 150 characters)
        excerpt: article.content 
          ? article.content.replace(/<[^>]*>/g, '').substring(0, 150) + '...'
          : '',
        // Use stored author or default
        author: article.author || 'Daily Ironing Team',
        // Estimate read time (average 200 words per minute)
        readTime: article.content 
          ? Math.max(1, Math.ceil(article.content.split(' ').length / 200))
          : 1,
        // Use stored image or default
        image: article.image_url || 'https://images.pexels.com/photos/1181467/pexels-photo-1181467.jpeg?auto=compress&cs=tinysrgb&w=800',
        // Format dates
        formattedDate: (article.published_at || article.created_at)
          ? new Date(article.published_at || article.created_at).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })
          : '',
        // Raw date for editing
        publishDate: article.published_at 
          ? new Date(article.published_at).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0],
        // SEO-friendly URL
        url: `/blog/${article.slug}`
      };
    },

    async getAll() {
      const { data, error } = await supabase
        .from('blog_articles')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data?.map(article => this.computeArticleFields(article)) || [];
    },

    async getById(id) {
      const { data, error } = await supabase
        .from('blog_articles')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return this.computeArticleFields(data);
    },

    async getPublished() {
      const { data, error } = await supabase
        .from('blog_articles')
        .select('*')
        .eq('is_published', true)
        .order('published_at', { ascending: false });
      
      if (error) throw error;
      return data?.map(article => this.computeArticleFields(article)) || [];
    },

    async getBySlug(slug) {
      const { data, error } = await supabase
        .from('blog_articles')
        .select('*')
        .eq('slug', slug)
        .eq('is_published', true)
        .single();
      
      if (error) throw error;
      return this.computeArticleFields(data);
    },

    async create(articleData) {
      // Generate slug if not provided
      if (!articleData.slug && articleData.title) {
        articleData.slug = articleData.title
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
      }
      
      const { data, error } = await supabase
        .from('blog_articles')
        .insert([articleData])
        .select()
        .single();
      
      if (error) throw error;
      return this.computeArticleFields(data);
    },

    async update(id, updates) {
      // Update slug if title changed and no custom slug provided
      if (updates.title && !updates.slug) {
        updates.slug = updates.title
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
      }
      
      const { data, error } = await supabase
        .from('blog_articles')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return this.computeArticleFields(data);
    },

    async delete(id) {
      const { error } = await supabase
        .from('blog_articles')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return true;
    }
  }
};